import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs, getDoc, doc, orderBy, onSnapshot } from 'firebase/firestore';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { db } from '../firebase';
import { ladeSpielerNamen, spielerName } from '../utils/spielerNamen';
import { useAuth } from '../contexts/AuthContext';
import { NFL_TEAMS } from '../data/nflTeams';
import { saisonIdZuName } from '../utils/saison';
import { istPlayoffSpiel, wocheName, wocheKurz } from '../utils/saisonPhasen';
import { farbenJeUserId, spielerFarbe, HIGHLIGHT_COLOR, DURCHSCHNITT_COLOR } from '../utils/spielerFarben';
import * as stat from '../utils/rankingStatistik';
import './RankingPage.css';

// Ranking: drei Reiter. "Tabelle" und "Statistik" kommen mit den punkte-Docs
// aus; "Mein Team" und die Insights brauchen die Spiele der Saison und die
// eigenen Tipps – die werden erst geladen, wenn der Reiter geöffnet wird.
//
// Die Statistik-Charts zeigen die Serien aus der Vergleichsauswahl:
// Ich, Bester, Durchschnitt, Letzter, einzelne Spieler oder alle.

const CHARTS = [
  { key: 'verlauf', label: '📈 Verlauf' },
  { key: 'woechentlich', label: '📊 Wöchentlich' },
  { key: 'platzierung', label: '🎯 Platzierung' },
  { key: 'uebersicht', label: '📋 Übersicht' },
  { key: 'genauigkeit', label: '🎯 Genauigkeit' },
  { key: 'insights', label: '💡 Insights' }
];

const TOOLTIP_STYLE = { backgroundColor: '#fff', border: '1px solid #ddd', color: '#000' };
const AUSWAHL_KEY = 'rankingVergleich';

function ladeAuswahl() {
  try {
    const roh = localStorage.getItem(AUSWAHL_KEY);
    if (roh) return { ...stat.STANDARD_AUSWAHL, ...JSON.parse(roh) };
  } catch (e) { /* kein localStorage */ }
  return stat.STANDARD_AUSWAHL;
}

function RankingPage() {
  const { saisonId } = useParams();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  const saisonName = saisonIdZuName(saisonId);

  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favoritenTeam, setFavoritenTeam] = useState(null);

  const [tab, setTab] = useState('tabelle');
  const [ansicht, setAnsicht] = useState('gesamt');
  const [chart, setChart] = useState('verlauf');
  const [auswahl, setAuswahl] = useState(ladeAuswahl);

  // Spiele + eigene Tipps: nur bei Bedarf
  const [alleSpiele, setAlleSpiele] = useState(null);
  const [userTipps, setUserTipps] = useState({});
  const [spieleLaden, setSpieleLaden] = useState(false);
  // Live-Ranking: ein Aggregat-Doc je Saison (systemConfig/liveRanking_{saison}),
  // von updateNFLScores während laufender Spiele geschrieben. Ein Listener, ein Doc.
  const [live, setLive] = useState(null);

  useEffect(() => {
    try { localStorage.setItem(AUSWAHL_KEY, JSON.stringify(auswahl)); } catch (e) { /* egal */ }
  }, [auswahl]);

  // ---------- Laden: Ranking ----------
  useEffect(() => {
    if (!uid) return undefined;
    let abgebrochen = false;

    const lade = async () => {
      setLoading(true);
      setAlleSpiele(null);
      try {
        const [punkteSnap, namen] = await Promise.all([
          getDocs(query(collection(db, 'punkte'), where('saison', '==', saisonId))),
          ladeSpielerNamen()
        ]);
        if (abgebrochen) return;
        setFavoritenTeam(namen[uid]?.favoritenTeam || null);

        const daten = punkteSnap.docs.map((p) => {
          const d = p.data();
          return {
            userId: d.userId,
            username: spielerName(namen, d.userId),
            gesamt: d.gesamt || 0,
            gesamtSpiele: d.gesamtSpiele || 0,
            gesamtBonus: d.gesamtBonus || 0,
            wochen: d.wochen || {},
            bonusfragen: d.bonusfragen || null,
            berechneteSpiele: d.spiele || {}
          };
        }).sort((a, b) => b.gesamt - a.gesamt);
        setRanking(daten);
      } catch (error) {
        console.error('Fehler beim Laden des Rankings:', error);
      } finally {
        if (!abgebrochen) setLoading(false);
      }
    };

    lade();
    return () => { abgebrochen = true; };
  }, [uid, saisonId]);

  // ---------- Live-Ranking (nur Tabelle) ----------
  useEffect(() => {
    if (!uid || tab !== 'tabelle') return undefined;
    const unsub = onSnapshot(doc(db, 'systemConfig', `liveRanking_${saisonId}`), snap => {
      const d = snap.exists() ? snap.data() : null;
      // Älter als 3 Stunden = hängen geblieben, ignorieren
      const frisch = d?.aktiv && d.stand && (Date.now() - new Date(d.stand).getTime()) < 3 * 60 * 60 * 1000;
      setLive(frisch ? d : null);
    }, error => console.error('Live-Ranking konnte nicht geladen werden:', error));
    return unsub;
  }, [uid, saisonId, tab]);

  // ---------- Laden: Spiele + Tipps (lazy) ----------
  const ladeSpieleUndTipps = useCallback(async () => {
    if (!uid || alleSpiele !== null || spieleLaden) return;
    setSpieleLaden(true);
    try {
      const [spieleSnap, tippSnap] = await Promise.all([
        getDocs(query(collection(db, 'spiele'), where('saison', '==', saisonName), orderBy('datum', 'asc'))),
        getDoc(doc(db, 'tipps', `${uid}_${saisonId}`))
      ]);
      setAlleSpiele(spieleSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setUserTipps(tippSnap.exists() ? (tippSnap.data().spiele || {}) : {});
    } catch (error) {
      console.error('Fehler beim Laden der Spiele:', error);
      setAlleSpiele([]);
    } finally {
      setSpieleLaden(false);
    }
  }, [uid, alleSpiele, spieleLaden, saisonName, saisonId]);

  const brauchtSpiele = tab === 'team' || (tab === 'statistik' && chart === 'insights');
  useEffect(() => {
    if (brauchtSpiele) ladeSpieleUndTipps();
  }, [brauchtSpiele, ladeSpieleUndTipps]);

  // ---------- Ableitungen ----------
  const playoffWochen = useMemo(() => {
    if (alleSpiele) {
      return Array.from(new Set(alleSpiele.filter(istPlayoffSpiel).map(s => s.week))).sort((a, b) => a - b);
    }
    // Ohne Spiele: Wochen > 18 aus den Punkte-Docs als Playoffs annehmen
    return stat.alleWochen(ranking).filter(w => w > 18);
  }, [alleSpiele, ranking]);

  const label = useCallback((week) => wocheName(week, playoffWochen.length ? playoffWochen : undefined), [playoffWochen]);
  const labelKurz = useCallback((week) => wocheKurz(week, playoffWochen.length ? playoffWochen : undefined), [playoffWochen]);

  const wochen = useMemo(() => stat.alleWochen(ranking), [ranking]);
  const regularWochen = wochen.filter(w => !playoffWochen.includes(w));
  const farben = useMemo(() => farbenJeUserId(ranking), [ranking]);
  const serienFarbe = useCallback((serie) =>
    (serie.typ === 'durchschnitt' ? DURCHSCHNITT_COLOR : spielerFarbe(farben, serie.userId, uid)),
  [farben, uid]);

  const serien = useMemo(() => stat.serienAuswahl(ranking, auswahl, uid), [ranking, auswahl, uid]);
  // Live-Punkte je Spieler: nur Spiele, die noch nicht im punkte-Doc verrechnet sind
  const liveJeSpieler = useMemo(() => {
    if (!live?.spieler) return {};
    const out = {};
    for (const z of ranking) {
      const details = live.spieler[z.userId]?.spiele || {};
      let summe = 0;
      for (const [spielId, d] of Object.entries(details)) {
        if (!z.berechneteSpiele?.[spielId]) summe += d.punkte || 0;
      }
      if (summe > 0) out[z.userId] = Math.round(summe * 10) / 10;
    }
    return out;
  }, [live, ranking]);
  const liveZaehlt = live?.aktiv && (ansicht === 'gesamt' || ansicht === 'spiele');
  const tabellenZeilen = useMemo(() => {
    if (!liveZaehlt) return stat.tabelle(ranking, ansicht);
    const mitLive = ranking.map(z => {
      const delta = liveJeSpieler[z.userId] || 0;
      return { ...z, live: delta, gesamt: z.gesamt + delta, gesamtSpiele: z.gesamtSpiele + delta };
    });
    return stat.tabelle(mitLive, ansicht);
  }, [ranking, ansicht, liveZaehlt, liveJeSpieler]);

  const ansichtTitel = () => {
    switch (ansicht) {
      case 'gesamt': return 'Gesamt';
      case 'spiele': return 'Nur Spieltipps';
      case 'bonus': return 'Nur Bonusfragen';
      case 'bonusPre': return 'Pre-Season Predictions';
      case 'bonusPlayoff': return 'Playoff Predictions';
      default: return label(ansicht);
    }
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="ranking-seite">
        <h1>🏆 Ranking {saisonName}</h1>
        <div className="karte karte--leer">⏳ Lade Ranking…</div>
      </div>
    );
  }

  return (
    <div className="ranking-seite">
      <h1>🏆 Ranking {saisonName}</h1>

      <div className="reiter" role="tablist">
        <button type="button" role="tab" className={`reiter__tab${tab === 'tabelle' ? ' ist-aktiv' : ''}`} onClick={() => setTab('tabelle')}>Tabelle</button>
        <button type="button" role="tab" className={`reiter__tab${tab === 'statistik' ? ' ist-aktiv' : ''}`} onClick={() => setTab('statistik')}>Statistik</button>
        {favoritenTeam && (
          <button type="button" role="tab" className={`reiter__tab${tab === 'team' ? ' ist-aktiv' : ''}`} onClick={() => setTab('team')}>⭐ {favoritenTeam}</button>
        )}
      </div>

      {ranking.length === 0 ? (
        <div className="karte karte--leer">Noch keine Punkte berechnet.</div>
      ) : (
        <>
          {tab === 'tabelle' && (
            <>
              <div className="werkzeuge">
                <select className="werkzeuge__select" value={ansicht} onChange={e => setAnsicht(isNaN(e.target.value) ? e.target.value : Number(e.target.value))} aria-label="Ansicht">
                  <optgroup label="Gesamt">
                    <option value="gesamt">🏆 Gesamt</option>
                    <option value="spiele">🏈 Nur Spieltipps</option>
                    <option value="bonus">🎁 Nur Bonusfragen</option>
                  </optgroup>
                  {regularWochen.length > 0 && (
                    <optgroup label="Regular Season">
                      {regularWochen.map(w => <option key={w} value={w}>{label(w)}</option>)}
                    </optgroup>
                  )}
                  {playoffWochen.filter(w => wochen.includes(w)).length > 0 && (
                    <optgroup label="Playoffs">
                      {playoffWochen.filter(w => wochen.includes(w)).map(w => <option key={w} value={w}>{label(w)}</option>)}
                    </optgroup>
                  )}
                  <optgroup label="Bonusfragen">
                    <option value="bonusPre">🎯 Pre-Season Predictions</option>
                    <option value="bonusPlayoff">🏆 Playoff Predictions</option>
                  </optgroup>
                </select>
              </div>

              <div className="karte">
                <h2>
                  {ansichtTitel()}
                  {liveZaehlt && <span className="live-chip" title="Zwischenstand laufender Spiele, ändert sich alle paar Minuten">🔴 live</span>}
                </h2>
                {liveZaehlt && (
                  <p className="karte__hinweis">
                    Inklusive Zwischenstand aus {Object.keys(live.spiele).length} laufenden {Object.keys(live.spiele).length === 1 ? 'Spiel' : 'Spielen'}. Stand {new Date(live.stand).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr.
                  </p>
                )}
                <table className="rang-tabelle">
                  <thead>
                    <tr>
                      <th className="spalte-rang">#</th>
                      <th>Spieler</th>
                      {ansicht === 'gesamt' && <th className="spalte-zahl spalte-nebenwert">Spiele</th>}
                      {ansicht === 'gesamt' && <th className="spalte-zahl spalte-nebenwert">Bonus</th>}
                      <th className="spalte-zahl">Punkte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabellenZeilen.map(z => (
                      <tr key={z.userId} className={z.userId === uid ? 'ist-ich' : ''}>
                        <td className="spalte-rang">{z.rang === 1 ? '🥇' : z.rang === 2 ? '🥈' : z.rang === 3 ? '🥉' : z.rang}</td>
                        <td>{z.username}{z.userId === uid && ' (Du)'}</td>
                        {ansicht === 'gesamt' && <td className="spalte-zahl spalte-nebenwert">{(z.gesamtSpiele || 0).toFixed(1)}</td>}
                        {ansicht === 'gesamt' && <td className="spalte-zahl spalte-nebenwert">{(z.gesamtBonus || 0).toFixed(1)}</td>}
                        <td className="spalte-zahl">
                          {z.punkte.toFixed(1)}
                          {z.live > 0 && <span className="live-delta">🔴 +{z.live.toFixed(1)}</span>}
                          {z.rueckstand > 0 && <span className="rueckstand">−{z.rueckstand.toFixed(1)}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {tab === 'statistik' && (
            <>
              <div className="chips" role="tablist">
                {CHARTS.map(c => (
                  <button key={c.key} type="button" className={`chip${chart === c.key ? ' ist-aktiv' : ''}`} onClick={() => setChart(c.key)}>
                    {c.label}
                  </button>
                ))}
              </div>

              {chart !== 'insights' && (
                <VergleichsAuswahl
                  ranking={ranking}
                  uid={uid}
                  auswahl={auswahl}
                  setAuswahl={setAuswahl}
                  farben={farben}
                  ohneDurchschnitt={chart === 'platzierung' || chart === 'genauigkeit'}
                />
              )}

              {chart === 'verlauf' && <VerlaufChart ranking={ranking} serien={serien} label={labelKurz} farbe={serienFarbe} />}
              {chart === 'woechentlich' && <WoechentlichChart ranking={ranking} serien={serien} label={labelKurz} farbe={serienFarbe} />}
              {chart === 'platzierung' && <PlatzierungChart ranking={ranking} serien={serien} label={labelKurz} farbe={serienFarbe} />}
              {chart === 'uebersicht' && <Uebersicht ranking={ranking} uid={uid} serien={serien} label={label} labelKurz={labelKurz} farbe={serienFarbe} />}
              {chart === 'genauigkeit' && <Genauigkeit ranking={ranking} serien={serien} label={labelKurz} farbe={serienFarbe} />}
              {chart === 'insights' && (
                spieleLaden || alleSpiele === null
                  ? <div className="karte karte--leer">⏳ Lade Spiele…</div>
                  : <Insights insights={stat.persoenlicheInsights(alleSpiele, userTipps)} />
              )}
            </>
          )}

          {tab === 'team' && favoritenTeam && (
            spieleLaden || alleSpiele === null
              ? <div className="karte karte--leer">⏳ Lade Spiele…</div>
              : <MeinTeam team={favoritenTeam} ranking={ranking} uid={uid} alleSpiele={alleSpiele} userTipps={userTipps} />
          )}
        </>
      )}
    </div>
  );
}

// ======================================================================
// Vergleichsauswahl
// ======================================================================

function VergleichsAuswahl({ ranking, uid, auswahl, setAuswahl, farben, ohneDurchschnitt }) {
  const andere = ranking.filter(u => u.userId !== uid);
  const alleGewaehlt = andere.length > 0 && andere.every(u => auswahl.spieler.includes(u.userId));
  const bester = stat.besterSpieler(ranking, uid);
  const letzter = stat.letzterSpieler(ranking, uid);

  const toggle = (feld) => setAuswahl(a => ({ ...a, [feld]: !a[feld] }));
  const toggleSpieler = (userId) => setAuswahl(a => ({
    ...a,
    spieler: a.spieler.includes(userId) ? a.spieler.filter(id => id !== userId) : [...a.spieler, userId]
  }));
  const toggleAlle = () => setAuswahl(a => ({
    ...a,
    ich: true,
    spieler: alleGewaehlt ? [] : andere.map(u => u.userId)
  }));

  return (
    <div className="vergleich">
      <div className="vergleich__zeile">
        <span className="vergleich__titel">Vergleich:</span>
        <button type="button" className={`chip chip--ich${auswahl.ich ? ' ist-aktiv' : ''}`} onClick={() => toggle('ich')}>Ich</button>
        {bester && (
          <button type="button" className={`chip${auswahl.bester ? ' ist-aktiv' : ''}`} onClick={() => toggle('bester')} title={bester.username}>
            👑 Bester
          </button>
        )}
        {!ohneDurchschnitt && (
          <button type="button" className={`chip chip--durchschnitt${auswahl.durchschnitt ? ' ist-aktiv' : ''}`} onClick={() => toggle('durchschnitt')}>
            Ø Durchschnitt
          </button>
        )}
        {letzter && letzter.userId !== bester?.userId && (
          <button type="button" className={`chip${auswahl.letzter ? ' ist-aktiv' : ''}`} onClick={() => toggle('letzter')} title={letzter.username}>
            📉 Letzter
          </button>
        )}
        <button type="button" className={`chip${alleGewaehlt ? ' ist-aktiv' : ''}`} onClick={toggleAlle}>
          👥 Alle
        </button>
      </div>
      {andere.length > 0 && (
        <div className="vergleich__zeile">
          <span className="vergleich__titel">Einzeln:</span>
          {andere.map(u => (
            <button
              key={u.userId}
              type="button"
              className={`chip${auswahl.spieler.includes(u.userId) ? ' ist-aktiv' : ''}`}
              onClick={() => toggleSpieler(u.userId)}
            >
              <span className="chip__farbe" style={{ background: spielerFarbe(farben, u.userId, uid) }} />
              {u.username}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ======================================================================
// Charts
// ======================================================================

function XAchse() {
  return <XAxis dataKey="week" stroke="#666" tick={{ fontSize: 11 }} interval={0} angle={-45} textAnchor="end" height={56} />;
}

function KeineAuswahl() {
  return <div className="karte karte--leer">Wähle oben mindestens einen Spieler aus.</div>;
}

function VerlaufChart({ ranking, serien, label, farbe }) {
  if (serien.length === 0) return <KeineAuswahl />;
  const daten = stat.verlaufDaten(ranking, serien, label);
  return (
    <div className="karte">
      <h2>📈 Punkteverlauf</h2>
      <p className="karte__hinweis">Gesamtpunkte im Saisonverlauf, Bonusfragen als letzter Punkt.</p>
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={daten}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAchse />
          <YAxis stroke="#666" width={36} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          <Legend />
          {serien.map(s => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              stroke={farbe(s)}
              strokeWidth={s.key === 'Ich' ? 3 : 2}
              strokeDasharray={s.typ === 'durchschnitt' ? '4 4' : undefined}
              dot={s.typ === 'durchschnitt' ? false : { r: 2 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function WoechentlichChart({ ranking, serien, label, farbe }) {
  if (serien.length === 0) return <KeineAuswahl />;
  const daten = stat.wochenDaten(ranking, serien, label);
  return (
    <div className="karte">
      <h2>📊 Punkte pro Woche</h2>
      <ResponsiveContainer width="100%" height={340}>
        <BarChart data={daten}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAchse />
          <YAxis stroke="#666" width={36} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          <Legend />
          {serien.map(s => (
            <Bar key={s.key} dataKey={s.key} fill={farbe(s)} fillOpacity={s.typ === 'durchschnitt' ? 0.6 : 1} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function PlatzierungChart({ ranking, serien, label, farbe }) {
  const spieler = stat.spielerSerien(serien);
  if (spieler.length === 0) return <KeineAuswahl />;
  const daten = stat.platzierungDaten(ranking, spieler, label);
  return (
    <div className="karte">
      <h2>🎯 Platzierung pro Woche</h2>
      <p className="karte__hinweis">Platz 1 ist oben. Geht die Linie nach oben, wurde es besser.</p>
      <ResponsiveContainer width="100%" height={340}>
        <AreaChart data={daten}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAchse />
          <YAxis reversed domain={[1, ranking.length]} allowDecimals={false} tickCount={ranking.length} stroke="#666" width={30} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          <Legend />
          {spieler.map(s => (
            <Area key={s.key} type="monotone" dataKey={s.key} stroke={farbe(s)} fill={farbe(s)} fillOpacity={s.key === 'Ich' ? 0.3 : 0.08} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function Uebersicht({ ranking, uid, serien, label, labelKurz, farbe }) {
  const spieler = stat.spielerSerien(serien);
  const { best, worst } = stat.besteUndSchwaechsteWochen(ranking, spieler);
  const form = stat.wochenDaten(ranking, serien, labelKurz, 5);
  const pie = stat.punkteVerteilung(ranking, uid);
  const hatPie = pie.some(p => p.value > 0);
  const nurIch = spieler.length === 1 && spieler[0].userId === uid;

  return (
    <>
      {hatPie && (
        <div className="karte">
          <h2>🎯 Woher kommen deine Punkte?</h2>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={pie} cx="50%" cy="50%" outerRadius={85} dataKey="value" labelLine={false}
                label={({ name, value, percent }) => (value > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : null)}>
                {pie.map((p, i) => <Cell key={i} fill={p.color} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {spieler.length === 0 ? <KeineAuswahl /> : (
        <div className="karten-grid">
          <div className="karte">
            <h3>🔥 Beste Wochen</h3>
            <div className="liste">
              {best.map((w, i) => (
                <div key={`${w.week}-${w.userId}`} className={`liste__zeile${i === 0 ? ' liste__zeile--top' : ''}`}>
                  <span>{['🥇', '🥈', '🥉'][i]} {label(w.week)}{!nurIch && ` · ${w.name}`}</span>
                  <span className="liste__wert wert--gut">{w.punkte.toFixed(1)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="karte">
            <h3>💔 Schwächste Wochen</h3>
            <div className="liste">
              {worst.map(w => (
                <div key={`${w.week}-${w.userId}`} className="liste__zeile">
                  <span>{label(w.week)}{!nurIch && ` · ${w.name}`}</span>
                  <span className="liste__wert wert--schlecht">{w.punkte.toFixed(1)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {serien.length > 0 && (
        <div className="karte">
          <h2>📊 Aktuelle Form (letzte 5 Wochen)</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={form}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="week" stroke="#666" tick={{ fontSize: 12 }} />
              <YAxis stroke="#666" width={36} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend />
              {serien.map(s => (
                <Bar key={s.key} dataKey={s.key} fill={farbe(s)} fillOpacity={s.typ === 'durchschnitt' ? 0.6 : 1} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );
}

function Genauigkeit({ ranking, serien, label, farbe }) {
  const spieler = stat.spielerSerien(serien);
  if (spieler.length === 0) return <KeineAuswahl />;
  const { weekly, totals } = stat.genauigkeitDaten(ranking, spieler, label);
  const F = stat.KATEGORIE_FARBEN;
  const einzeln = spieler.length === 1;
  const einzig = spieler[0];

  return (
    <>
      <div className="karte">
        <h2>🎯 Tipp-Genauigkeit pro Woche</h2>
        <p className="karte__hinweis">
          {einzeln
            ? 'Exakt, Tendenz, Sieger, Falsch – gestapelt je Woche.'
            : 'Ein Stapel je Spieler und Woche. Deckkraft: Exakt 100 % · Tendenz 70 % · Sieger 50 % · Falsch 30 %.'}
        </p>
        {!einzeln && (
          <div className="legende">
            {spieler.map(s => (
              <span key={s.key} className="legende__eintrag">
                <span className="legende__farbe" style={{ background: farbe(s) }} />{s.key}
              </span>
            ))}
          </div>
        )}
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={weekly}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAchse />
            <YAxis stroke="#666" width={30} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            {einzeln && <Legend />}
            {einzeln ? (
              <>
                <Bar dataKey={`${einzig.key}_exakt`} stackId="a" fill={F.exakt} name="Exakt" />
                <Bar dataKey={`${einzig.key}_tendenz`} stackId="a" fill={F.tendenz} name="Tendenz" />
                <Bar dataKey={`${einzig.key}_sieger`} stackId="a" fill={F.sieger} name="Sieger" />
                <Bar dataKey={`${einzig.key}_falsch`} stackId="a" fill={F.falsch} name="Falsch" />
              </>
            ) : spieler.map(s => (
              <React.Fragment key={s.key}>
                <Bar dataKey={`${s.key}_exakt`} stackId={s.key} fill={farbe(s)} name={`${s.key} exakt`} />
                <Bar dataKey={`${s.key}_tendenz`} stackId={s.key} fill={farbe(s)} fillOpacity={0.7} name={`${s.key} Tendenz`} />
                <Bar dataKey={`${s.key}_sieger`} stackId={s.key} fill={farbe(s)} fillOpacity={0.5} name={`${s.key} Sieger`} />
                <Bar dataKey={`${s.key}_falsch`} stackId={s.key} fill={farbe(s)} fillOpacity={0.3} name={`${s.key} falsch`} />
              </React.Fragment>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="karte">
        <h2>{einzeln ? '🎯 Gesamt-Verteilung' : '🎯 Vergleich'}</h2>
        {einzeln ? (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Exakt', value: totals[0].exakt, color: F.exakt },
                  { name: 'Tendenz', value: totals[0].tendenz, color: F.tendenz },
                  { name: 'Sieger', value: totals[0].sieger, color: F.sieger },
                  { name: 'Falsch', value: totals[0].falsch, color: F.falsch }
                ]}
                cx="50%" cy="50%" outerRadius={90} dataKey="value" labelLine={false}
                label={({ name, value, percent }) => (value > 0 ? `${name} ${value} (${(percent * 100).toFixed(0)}%)` : null)}
              >
                {[F.exakt, F.tendenz, F.sieger, F.falsch].map((c, i) => <Cell key={i} fill={c} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, totals.length * 44)}>
            <BarChart data={totals} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis type="number" stroke="#666" />
              <YAxis dataKey="name" type="category" stroke="#666" width={90} tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend />
              <Bar dataKey="exakt" fill={F.exakt} name="Exakt" />
              <Bar dataKey="tendenz" fill={F.tendenz} name="Tendenz" />
              <Bar dataKey="sieger" fill={F.sieger} name="Sieger" />
              <Bar dataKey="falsch" fill={F.falsch} name="Falsch" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </>
  );
}

function ZweiTeilePie({ titel, a, b, sub }) {
  return (
    <div>
      <h3 style={{ textAlign: 'center' }}>{titel}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={[a, b]} cx="50%" cy="50%" outerRadius={70} dataKey="value" labelLine={false}
            label={({ value }) => (value > 0 ? value : '')}>
            <Cell fill="#3f3f3f" />
            <Cell fill="rgba(100, 3, 100, 0.88)" />
          </Pie>
          <Tooltip contentStyle={TOOLTIP_STYLE} />
        </PieChart>
      </ResponsiveContainer>
      <div style={{ textAlign: 'center', fontSize: 13 }}>{sub}</div>
    </div>
  );
}

function Insights({ insights }) {
  if (!insights) return <div className="karte karte--leer">Noch keine abgeschlossenen Spiele mit Tipps.</div>;
  const { homeAway: ha, regularPlayoff: rp, teamPerformance: tp } = insights;
  const keineTipps = ha.verteilung.gesamt === 0 && rp.verteilung.gesamt === 0;
  if (keineTipps) return <div className="karte karte--leer">Noch keine abgeschlossenen Spiele mit Tipps.</div>;

  return (
    <>
      <div className="karte">
        <h2>🏠 Heim vs. Auswärts</h2>
        <p className="karte__hinweis">Wie gut liegst du, wenn das Heimteam gewinnt – und wenn der Gast gewinnt?</p>
        <div className="karten-grid">
          <ZweiTeilePie titel="Richtige Tipps" a={{ name: 'Heimsiege', value: ha.verteilung.heimsiege }} b={{ name: 'Auswärtssiege', value: ha.verteilung.awaysiege }} sub={`${ha.verteilung.gesamt} richtige Tipps`} />
          <ZweiTeilePie titel="Bei Heimsieg" a={{ name: 'Richtig', value: ha.heimsiege.richtig }} b={{ name: 'Falsch', value: ha.heimsiege.falsch }} sub={`${ha.heimsiege.prozent}% richtig (${ha.heimsiege.richtig}/${ha.heimsiege.gesamt})`} />
          <ZweiTeilePie titel="Bei Auswärtssieg" a={{ name: 'Richtig', value: ha.awaysiege.richtig }} b={{ name: 'Falsch', value: ha.awaysiege.falsch }} sub={`${ha.awaysiege.prozent}% richtig (${ha.awaysiege.richtig}/${ha.awaysiege.gesamt})`} />
        </div>
        {ha.heimsiege.gesamt > 0 && ha.awaysiege.gesamt > 0 && ha.heimsiege.prozent !== ha.awaysiege.prozent && (
          <div className="insight">
            💡 {ha.heimsiege.prozent > ha.awaysiege.prozent
              ? `Heimsiege liegen dir besser: ${ha.heimsiege.prozent}% vs. ${ha.awaysiege.prozent}%`
              : `Auswärtssiege liegen dir besser: ${ha.awaysiege.prozent}% vs. ${ha.heimsiege.prozent}%`}
          </div>
        )}
      </div>

      {rp.verteilung.playoff > 0 && (
        <div className="karte">
          <h2>🏈 Regular Season vs. Playoffs</h2>
          <div className="karten-grid">
            <ZweiTeilePie titel="Getippte Spiele" a={{ name: 'Regular', value: rp.verteilung.regular }} b={{ name: 'Playoffs', value: rp.verteilung.playoff }} sub={`${rp.verteilung.gesamt} Spiele`} />
            <ZweiTeilePie titel="Regular Season" a={{ name: 'Richtig', value: rp.regular.richtig }} b={{ name: 'Falsch', value: rp.regular.falsch }} sub={`${rp.regular.prozent}% richtig`} />
            <ZweiTeilePie titel="Playoffs" a={{ name: 'Richtig', value: rp.playoff.richtig }} b={{ name: 'Falsch', value: rp.playoff.falsch }} sub={`${rp.playoff.prozent}% richtig`} />
          </div>
        </div>
      )}

      <div className="karten-grid">
        <div className="karte">
          <h3 className="wert--gut">✅ Deine besten Teams</h3>
          <TeamListe teams={tp.beste} klasse="wert--gut" />
        </div>
        <div className="karte">
          <h3 className="wert--schlecht">⚠️ Deine schwierigsten Teams</h3>
          <TeamListe teams={tp.schlechteste} klasse="wert--schlecht" />
        </div>
      </div>
    </>
  );
}

function TeamListe({ teams, klasse }) {
  if (teams.length === 0) return <p className="karte__hinweis">Noch zu wenig Spiele (mindestens 2 je Team).</p>;
  return (
    <div className="liste">
      {teams.map((t, i) => (
        <div key={t.team} className={`liste__zeile${i === 0 && klasse === 'wert--gut' ? ' liste__zeile--top' : ''}`}>
          <span className="liste__links">
            <img src={NFL_TEAMS[t.team]?.logo} alt="" />
            <span>
              <div>{t.teamName}</div>
              <div className="liste__sub">{t.richtig}/{t.gesamt} richtig</div>
            </span>
          </span>
          <span className={`liste__wert ${klasse}`}>{t.prozent}%</span>
        </div>
      ))}
    </div>
  );
}

// ======================================================================
// Mein Team
// ======================================================================

function MeinTeam({ team, ranking, uid, alleSpiele, userTipps }) {
  const s = stat.favoritenTeamStats(alleSpiele, userTipps, team);
  const riv = stat.divisionRivalitaeten(alleSpiele, userTipps, team);
  const favRanking = stat.favoritenRanking(ranking, alleSpiele, team);
  const info = NFL_TEAMS[team];

  return (
    <>
      <div className="team-banner">
        <img src={info?.logo} alt="" />
        <div>
          <div className="team-banner__name">{info?.name || team}</div>
          <div className="team-banner__sub">Dein Lieblingsteam · Saison-Record {s?.teamRecord || '0-0'}</div>
        </div>
      </div>

      {s && (
        <div className="kacheln">
          <div className="kachel" style={{ borderColor: '#28a745' }}>
            <div className="kachel__wert wert--gut">{s.genauigkeit}%</div>
            <div className="kachel__label">Tipp-Genauigkeit</div>
            <div className="kachel__sub">{s.richtig} / {s.gespielt} richtig</div>
          </div>
          <div className="kachel">
            <div className="kachel__wert">{s.gespielt}</div>
            <div className="kachel__label">Spiele getippt</div>
            <div className="kachel__sub">von {s.gesamt} gespielten</div>
          </div>
          <div className="kachel" style={{ borderColor: '#dc3545' }}>
            <div className="kachel__wert wert--schlecht">{s.laengsteStreak}</div>
            <div className="kachel__label">🔥 Längste Streak</div>
          </div>
          <div className="kachel">
            <div className="kachel__wert">{s.aktuelleStreak}</div>
            <div className="kachel__label">📈 Aktuelle Streak</div>
            <div className="kachel__sub">{s.aktuelleStreak > 0 ? 'läuft' : 'unterbrochen'}</div>
          </div>
        </div>
      )}

      {s && s.gespielt > 0 && (
        <div className="karte">
          <h3>📊 Deine Tipps bei {team}-Spielen</h3>
          <div className="kacheln">
            {[['exakt', '🎯 Exakt'], ['tendenz', '📈 Tendenz'], ['sieger', '👑 Sieger'], ['falsch', '❌ Falsch']].map(([k, l]) => (
              <div key={k} className="kachel" style={{ borderColor: stat.KATEGORIE_FARBEN[k] }}>
                <div className="kachel__wert">{s[k]}</div>
                <div className="kachel__label">{l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {riv && riv.rivalitaeten.length > 0 && (
        <div className="karte">
          <h3>⚔️ {riv.divisionName}: Rivalen</h3>
          <div className="liste">
            {riv.rivalitaeten.map(r => (
              <div key={r.gegner} className="liste__zeile">
                <span className="liste__links">
                  <img src={NFL_TEAMS[r.gegner]?.logo} alt="" />
                  <span>
                    <div>vs. {r.gegnerName}</div>
                    <div className="liste__sub">{r.gesamt === 0 ? 'noch kein Spiel' : `${r.richtig}/${r.gesamt} richtig`}</div>
                  </span>
                </span>
                <span className={`liste__wert ${r.gesamt === 0 ? '' : r.prozent >= 75 ? 'wert--gut' : r.prozent >= 50 ? 'wert--mittel' : 'wert--schlecht'}`}>
                  {r.gesamt === 0 ? '⏳' : `${r.prozent}%`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="karte">
        <h3>🏈 {team}-Ranking</h3>
        <p className="karte__hinweis">Punkte aller Spieler nur aus {team}-Spielen (anteilig aus den Wochenpunkten geschätzt).</p>
        {favRanking.every(r => r.punkte === 0) ? (
          <p className="karte__hinweis">Noch keine Punkte aus {team}-Spielen.</p>
        ) : (
          <table className="rang-tabelle">
            <thead>
              <tr><th className="spalte-rang">#</th><th>Spieler</th><th className="spalte-zahl">Punkte</th></tr>
            </thead>
            <tbody>
              {favRanking.map((r, i) => (
                <tr key={r.userId} className={r.userId === uid ? 'ist-ich' : ''}>
                  <td className="spalte-rang">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}</td>
                  <td>{r.username}{r.userId === uid && ' (Du)'}</td>
                  <td className="spalte-zahl">{r.punkte.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

export default RankingPage;
